// ** React Imports
import { createContext, useMemo, useContext } from 'react'

// ** Config
import { campaignCategoryUrl, campaignUrl, filterUrl, segmentsListUrl } from 'src/configs/api'

import { AuthContext } from './AuthContext'
import { CampaignContext } from './CampaignContext'
import { useAPI } from 'src/utils/api'
import { useToast } from 'src/@core/components/custom-Toast/customToast'

// ** Defaults

const PromotionalCampaignContext = createContext()

const PromotionalCampaignProvider = ({ children }) => {
  const { showSuccess, showError, showWarning } = useToast()

  // The save succeeds with advisory suppression_warnings — a list that
  // can't affect any channel this campaign sends on. Showing them is the
  // only way the user learns the list is inert here.
  const announceSuppressionWarnings = campaign => {
    ;(campaign?.suppression_warnings || []).forEach(warning => {
      showWarning(`${warning.name}: ${warning.reason}`)
    })
  }
  const {
    setIsCampaignSetupModified,
    setCampaignSaveStatus,
    setIsEmailContentModified,
    setIsWhatsappContentModified,
    setIsSmsContentModified,
    setIsRCSContentModified,
    campaignDirection,
    setCampaignMessageID,
    setEmailChannelId,
    emailChannelId,
    setWhatsappChannelId,
    rcsChannelId,
    setRCSChannelId,
    smsChannelId,
    setSmsChannelId,
    setEmailCampaignChannelId,
    setWhatsappCampaignChannelId,
    setSmsCampaignChannelId,
    selectedChannels,
    setSelectedChannels,
    isCampaignChannelsModified,
    setPreviousUserChannels,
    updatedEnabledChannels,
    updatedDisabledChannels,
    enableEmail,
    setEnableEmail,
    enableWhatsapp,
    setEnableWhatsapp,
    enableSms,
    setEnableSms,
    enableRCS,
    setEnableRCS,
    campaignname,
    setCampaignname,
    selectedSegments,
    setSelectedSegments,
    setAudienceSegments,
    selectedSuppressionLists,
    setSelectedSuppressionLists,
    selectedSeedLists,
    setSelectedSeedLists,
    selectCategories,
    setCampaignSchedule,
    setSelectCategories,
    selectAudienceSettings,
    setSelectAudienceSettings,
    setCampaignListData,
    setCampaignsCount,
    setCampaignCategoryList,
    setArchiveListData,
    setPreHeader,
    selectedCampaign,
    setSelectedCampaign,
    setCampaignSMSData,
    setCampaignWhatsappData,
    setCampaignEmailData,
    setPreviewSMSData,
    setPreviewWhatsappData,
    setPreviewEmailData,
    selectedTemplateId,
    setSelectedTemplateId,
    setSelectedTemplateName,
    previousSelectedTemplateId,
    setPreviousSelectedTemplateId,
    tags,
    setTags,
    setEmailCampaignContentId,
    setWhatsappCampaignContentId,
    setSmsCampaignContentId,
    setCampaignDate,
    setCampaignChannelData,
    trackEngagement,
    setTrackEngagement,
    setCampaignFilterList,
    setCampaignOtherFilterList,
    setHasTagFilter,
    fetchWhatsappTranslation,
    fetchRcsTranslation,
    fetchEmailTranslation,
    fetchSmsTranslation,
    fetchWhatsappTemplateTranslation,
    fetchEmailTemplateTranslation,
    fetchSMSTemplateTranslation,
    fetchRCSTemplateTranslation,
    getTemplate
  } = useContext(CampaignContext)
  const api = useAPI()

  // const getEmailPayload=()=>{
  //   return(
  //     {
  //       "configuration": {},
  //       "channelConfiguration": {
  //         "channelConfigId": selectedEmailConfigId,
  //         "channelConfigName": "Test Email",
  //         "additionalInfo": {
  //           "senderEmail": fromEmail,
  //           "replayToEmail": replyToEmail
  //         }
  //       },
  //       "layoutGroups": [
  //         {
  //           // "groupId": "019b35d8-a3eb-784a-a433-6064816498bc",
  //           "layoutType": "HEADER",
  //           "configuration": {},
  //           "contentUnits": [
  //             {
  //               // "unitId": "019b35d8-a3ec-7d86-8f81-e98c8c48b446",
  //               "unitName": "Header Title",
  //               "components": [
  //                 {
  //                   // "contentId": "019b35d8-a3f0-7624-92fe-4f89b5ca4941",
  //                   "componentKey": "subject",
  //                   "componentCategory": "HEADER",
  //                   "dataType": "TEXT",
  //                   "translations": {
  //                     "en": campaignsubject
  //                   }
  //                 }
  //               ]
  //             }
  //           ]
  //         },
  //         {
  //           // "groupId": "019b35d8-a3f4-79b2-8718-1c9e37ea02b2",
  //           "layoutType": "BLOCK",
  //           "configuration": {},
  //           "contentUnits": [
  //             {
  //               // "unitId": "019b35d8-a3f5-7c58-98f5-26dcc9f837cb",
  //               "unitName": "Body Content",
  //               "components": [
  //                 {
  //                   // "contentId": "019b35d8-a3f7-7c91-ae99-389711db06a4",
  //                   "componentKey": "body_html",
  //                   "componentCategory": "BODY",
  //                   "dataType": "TEXT",
  //                   "translations": {
  //                     "en": emailValue
  //                   }
  //                 }
  //               ]
  //             }
  //           ]
  //         }
  //       ]
  //     }
  //   )
  // }

  const createCampaign = async _validationStatus => {
    try {
      const inputData = {
        name: campaignname,
        audience_segment_ids: selectedSegments,
        // Always sent, never omitted: the backend treats an absent key as
        // "leave unchanged" and [] as "clear every list", so dropping it would
        // make removing the last chip impossible.
        suppression_list_ids: selectedSuppressionLists || [],
        seed_list_ids: selectedSeedLists || [],
        smart_channel_setting: selectAudienceSettings,
        campaignChannels: getChannelIdList(selectedChannels),
        template_id: selectedTemplateId,
        tags: tags,
        track_engagement: trackEngagement,
        category: selectCategories
      }
      const response = await api.post(campaignUrl, inputData, {
        params: {
          // validation: validationStatus
          validation: false
        },
        skipGlobalToast: true
      })
      setIsCampaignSetupModified(false)
      const selected_campaign = response?.data?.data
      announceSuppressionWarnings(selected_campaign)
      localStorage.setItem('selectedCampaignStore', JSON.stringify(selected_campaign))
      setSelectedCampaign(selected_campaign)
      setCampaignname(selected_campaign?.name)
      setSelectAudienceSettings(selected_campaign?.smart_channel_setting)
      setSelectedSegments(selected_campaign?.audience_segment_ids || [])
      setAudienceSegments(selected_campaign?.audience_segments || [])
      setSelectedSuppressionLists(selected_campaign?.suppression_list_ids || [])
      setSelectedSeedLists(selected_campaign?.seed_list_ids || [])
      setSelectedTemplateId(selected_campaign?.template_id)
      setPreviousSelectedTemplateId(selected_campaign?.template_id)

      setPreHeader(selected_campaign?.pre_headers)
      setTags(selected_campaign?.tags)
      setTrackEngagement(selected_campaign?.track_engagement)
      setSelectedChannels(selected_campaign?.campaignChannels || [])
      setPreviousUserChannels(selected_campaign?.campaignChannels || [])
      updateChannelStates(selected_campaign?.campaignChannels || [])
      setCampaignDate(selected_campaign?.start_date)
      setCampaignSchedule(selected_campaign?.status)
      setSelectCategories(selected_campaign?.category)
      const fetchTemplate = async () => {
        if (!selected_campaign?.template_id) {
          return
        }

        try {
          const responseTemplate = await getTemplate(selected_campaign.template_id)

          const templateChannelDetails = responseTemplate?.channels
          const campaignChannelDetails = selected_campaign?.campaignChannels

          const campaignChannelMap = new Map(campaignChannelDetails.map(ch => [ch.channelId, ch]))

          templateChannelDetails.forEach(templateChannel => {
            const campaignChannel = campaignChannelMap.get(templateChannel.channelId)

            if (!campaignChannel) {
              return
            }

            switch (templateChannel.channelName.toLowerCase()) {
              case 'email':
                fetchEmailTemplateTranslation(
                  selected_campaign?.template_id,
                  // templateChannel,
                  campaignChannel.channelId // ✅ pass campaign channelId
                )
                break

              case 'whatsapp':
                fetchWhatsappTemplateTranslation(
                  selected_campaign?.template_id,

                  campaignChannel.channelId
                )
                break

              case 'sms':
                fetchSMSTemplateTranslation(
                  selected_campaign?.template_id,

                  campaignChannel.channelId
                )
                break

              case 'rcs':
                fetchRCSTemplateTranslation(
                  selected_campaign?.template_id,

                  campaignChannel.channelId
                )
                break

              default:
                break
            }
          })
        } catch {
          // errors are already shown by the api client's response interceptor
        }
      }

      fetchTemplate()

      if (selected_campaign?.audience_segments?.length) {
        getOminiChannelData(selected_campaign.audience_segments[0].name)
      }
      if (response?.status === 200) {
        setCampaignSaveStatus({ type: 'success', message: 'Campaign saved successfully' })
        return true
      }
      return false
    } catch (error) {
      if (error) {
        setIsCampaignSetupModified(true)
        setCampaignSaveStatus({
          type: 'error',
          message: error?.response?.data?.message || error?.response?.data?.error || 'Failed to save campaign'
        })
      }
    }
  }

  const deleteCampaign = async campaignId => {
    try {
      const response = await api.delete(`${campaignUrl}/${campaignId}`)
      if (response) {
        showSuccess('Campaign deleted successfully')
        getCampaignList()
      }
    } catch {
      // errors are already shown by the api client's response interceptor
    }
  }

  const cloneCampaign = async (campaignId, campaignName) => {
    const inputData = {
      campaignName: campaignName
    }

    const response = await api.post(`${campaignUrl}/${campaignId}/clone`, inputData)
    if (response) {
      showSuccess('Campaign cloned successfully')
    }
    return response
  }

  const campaignBulkAction = async (selectedIDList, bulkAction) => {
    try {
      const inputData = {
        campaigns: selectedIDList,
        action: bulkAction
      }
      await api.put(`${campaignUrl}/status`, inputData)
    } catch {
      // errors are already shown by the api client's response interceptor
    }
  }

  const updateCampaignRecordStatus = async (campaignId, recordStatus) => {
    try {
      const inputData = {
        action: recordStatus,
        startDate: null
      }
      const response = await api.put(`${campaignUrl}/${campaignId}/status`, inputData)
      if (response) {
        showSuccess('Campaign archived successfully')
      }
    } catch {
      // errors are already shown by the api client's response interceptor
    }
  }
  const archivePromotionalCampaign = async (campaignId, isArchiveValue) => {
    try {
      const inputData = {
        isArchive: isArchiveValue
      }

      const response = await api.put(`${campaignUrl}/${campaignId}/archive`, inputData)

      if (response) {
        showSuccess(response?.data?.data || 'Campaign status updated successfully')
      }
    } catch {
      // errors are already shown by the api client's response interceptor
    }
  }
  const updateCampaign = async (id, _validationStatus) => {
    if (isCampaignChannelsModified) {
      // ← Run disable FIRST, then enable (sequential, not parallel)
      if (updatedDisabledChannels.length > 0) {
        await disableCampaignChannels(id, updatedDisabledChannels)
      }
      if (updatedEnabledChannels.length > 0) {
        await enableCampaignChannels(id, updatedEnabledChannels)
      }
    }
    console.log(
      'campaignChannels: getCampaignChannelIdList(selectedChannels),',
      getCampaignChannelIdList(selectedChannels)
    )
    try {
      const inputData = {
        name: campaignname,
        audience_segment_ids: selectedSegments,
        // Always sent, never omitted: the backend treats an absent key as
        // "leave unchanged" and [] as "clear every list", so dropping it would
        // make removing the last chip impossible.
        suppression_list_ids: selectedSuppressionLists || [],
        seed_list_ids: selectedSeedLists || [],
        smart_channel_setting: selectAudienceSettings,
        campaignChannels: getCampaignChannelIdList(selectedChannels),
        template_id: selectedTemplateId,
        // previousSelectedTemplateId !== selectedTemplateId && selectedTemplateId !== null ? selectedTemplateId : null,
        tags: tags,
        track_engagement: trackEngagement,
        category: selectCategories
      }

      const response = await api.put(`${campaignUrl}/${id}`, inputData, {
        params: {
          validation: false
        },
        skipGlobalToast: true
      })
      const selected_campaign = response?.data?.data

      // Detect whether the template itself changed on this save — if so, the
      // content mapped in from the (new) template needs to be re-fetched so
      // the form/preview reflect the newly selected template instead of the
      // previous one.
      const templateChanged = previousSelectedTemplateId !== selected_campaign?.template_id
      announceSuppressionWarnings(selected_campaign)

      setIsCampaignSetupModified(false)
      localStorage.setItem('selectedCampaignStore', JSON.stringify(selected_campaign))
      setSelectedCampaign(selected_campaign)
      setCampaignname(selected_campaign?.name)
      setSelectAudienceSettings(selected_campaign?.smart_channel_setting)
      setSelectedSegments(selected_campaign?.audience_segment_ids || [])
      setAudienceSegments(selected_campaign?.audience_segments || [])
      setSelectedSuppressionLists(selected_campaign?.suppression_list_ids || [])
      setSelectedSeedLists(selected_campaign?.seed_list_ids || [])
      setPreHeader(selected_campaign?.pre_headers)
      setSelectedTemplateId(selected_campaign?.template_id)
      setPreviousSelectedTemplateId(selected_campaign?.template_id)
      setTags(selected_campaign?.tags)
      setTrackEngagement(selected_campaign?.track_engagement)
      setSelectedChannels(selected_campaign?.campaignChannels || [])
      setPreviousUserChannels(selected_campaign?.campaignChannels || [])
      updateChannelStates(selected_campaign?.campaignChannels || [])
      setCampaignDate(selected_campaign?.start_date)
      setCampaignSchedule(selected_campaign?.status)
      setSelectCategories(selected_campaign?.category)
      if (selected_campaign?.audience_segments?.length) {
        getOminiChannelData(selected_campaign.audience_segments[0].name)
      }

      if (templateChanged && selected_campaign?.template_id) {
        const fetchTemplate = async () => {
          try {
            const responseTemplate = await getTemplate(selected_campaign.template_id)

            const templateChannelDetails = responseTemplate?.channels
            const campaignChannelDetails = selected_campaign?.campaignChannels

            const campaignChannelMap = new Map((campaignChannelDetails || []).map(ch => [ch.channelId, ch]))

            templateChannelDetails?.forEach(templateChannel => {
              const campaignChannel = campaignChannelMap.get(templateChannel.channelId)

              if (!campaignChannel) {
                return
              }

              switch (templateChannel.channelName.toLowerCase()) {
                case 'email':
                  fetchEmailTemplateTranslation(selected_campaign?.template_id, campaignChannel.channelId)
                  break

                case 'whatsapp':
                  fetchWhatsappTemplateTranslation(selected_campaign?.template_id, campaignChannel.channelId)
                  break

                case 'sms':
                  fetchSMSTemplateTranslation(selected_campaign?.template_id, campaignChannel.channelId)
                  break

                case 'rcs':
                  fetchRCSTemplateTranslation(selected_campaign?.template_id, campaignChannel.channelId)
                  break

                default:
                  break
              }
            })
          } catch {
            // errors are already shown by the api client's response interceptor
          }
        }

        fetchTemplate()
      }

      if (response?.status === 200) {
        setCampaignSaveStatus({ type: 'success', message: 'Campaign updated successfully' })
        return true
      }
      return false
    } catch (error) {
      if (error) {
        setIsCampaignSetupModified(true)
        setCampaignSaveStatus({
          type: 'error',
          message: error?.response?.data?.message || error?.response?.data?.error || 'Failed to update campaign'
        })
      }

      return false
    }
  }

  const updateFinalCampaign = async (id, _campaignStatus, _campaignSendDate, validationStatus, payload) => {
    try {
      // v1/api/c/campaigns/{campaignId}/status
      const response = await api.put(`${campaignUrl}/${id}/status`, payload, {
        params: {
          validation: validationStatus
        }
      })
      if (response?.status === 200) {
        return true
      }
      return false
    } catch {
      // errors are already shown by the api client's response interceptor
    }
  }

  const getCampaignList = async payload => {
    try {
      const response = await api.post(`${campaignUrl}/search`, payload)
      setCampaignListData(response?.data?.data?.campaigns || [])
      setCampaignsCount(response?.data?.data?.total || 0)
    } catch {
      // errors are already shown by the api client's response interceptor
    }
  }

  const getPromotionalCampaignFilterList = async () => {
    try {
      const response = await api.get(`${filterUrl}/campaigns?type=promotional`)

      const filters = response?.data?.data?.filters || []
      const otherFilters = response?.data?.data?.otherFilters || []

      const tagsFilter = filters.find(f => f.key === 'tags')
      setHasTagFilter(!!tagsFilter)
      setCampaignFilterList(tagsFilter ? tagsFilter.options.map(o => o.value) : [])
      setCampaignOtherFilterList(otherFilters)

      const initialSelectedFilters = {}
      otherFilters.forEach(f => {
        initialSelectedFilters[f.key] = []
      })

      return initialSelectedFilters
    } catch {
      // errors are already shown by the api client's response interceptor
    }
  }

  const getArchiveList = async () => {
    try {
      const response = await api.get(`${campaignUrl}?recordStatus=archived`)
      setArchiveListData(response?.data?.data?.campaigns || [])
      showSuccess('Archive list loaded successfully')
    } catch {
      // errors are already shown by the api client's response interceptor
    }
  }

  const getCampaign = async id => {
    try {
      const response = await api.get(`${campaignUrl}/${id}`)

      const selected_campaign = response?.data?.data
      localStorage.setItem('selectedCampaignStore', JSON.stringify(selected_campaign))
      setSelectedCampaign(selected_campaign)
      setCampaignname(selected_campaign?.name)
      setSelectAudienceSettings(selected_campaign?.smart_channel_setting)
      setSelectedSegments(selected_campaign?.audience_segment_ids || [])
      setAudienceSegments(selected_campaign?.audience_segments || [])
      setSelectedSuppressionLists(selected_campaign?.suppression_list_ids || [])
      setSelectedSeedLists(selected_campaign?.seed_list_ids || [])
      setPreHeader(selected_campaign?.pre_headers)
      setSelectedTemplateId(selected_campaign?.template_id)
      setPreviousSelectedTemplateId(selected_campaign?.template_id)
      setSelectedTemplateName(selected_campaign?.template_name)
      setTags(selected_campaign?.tags)
      setTrackEngagement(selected_campaign?.track_engagement)
      setSelectedChannels(selected_campaign?.campaignChannels || [])
      setPreviousUserChannels(selected_campaign?.campaignChannels || [])
      updateChannelStates(selected_campaign?.campaignChannels || [])
      setCampaignDate(selected_campaign?.start_date)
      setCampaignSchedule(selected_campaign?.status)
      setSelectCategories(selected_campaign?.category)
      if (selected_campaign?.audience_segments?.length) {
        getOminiChannelData(selected_campaign.audience_segments[0].name)
      }

      return true
    } catch {
      return false
    }
  }

  const getCampaignMessage = async campaignChannelId => {
    try {
      const response = await api.get(`${campaignUrl}/${selectedCampaign?.id}/campaignChannels/${campaignChannelId}`)
      const messageData = response?.data?.data
      if (messageData) {
        if (messageData.channelName.toLowerCase() === 'sms') {
          setCampaignSMSData(messageData)
          setPreviewSMSData(messageData)
        } else if (messageData.channelName.toLowerCase() === 'whatsapp') {
          setCampaignWhatsappData(messageData)
          setPreviewWhatsappData(messageData)
        } else if (messageData.channelName.toLowerCase() === 'email') {
          setCampaignEmailData(messageData)
          setPreviewEmailData(messageData)
        }
      }
    } catch {
      // errors are already shown by the api client's response interceptor
    }
  }

  const updateSMSMessage = async (campaignId, smsCampaignChannelId, smsPayload, validate, type) => {
    setCampaignSaveStatus(null)
    try {
      const response = await api.put(
        `${campaignUrl}/${campaignId}/channeldata/${smsChannelId}`,
        smsPayload,

        {
          params: {
            validation: false
          },
          skipGlobalToast: true
        }
      )

      setIsSmsContentModified(false)
      let channelSmsId = response?.data?.data?.channelId

      fetchSmsTranslation(channelSmsId, type)

      if (response?.status === 200) {
        setCampaignSaveStatus({ type: 'success', message: 'SMS Campaign message updated successfully' })
        return true
      }
      return false
    } catch (error) {
      if (error) {
        setIsSmsContentModified(true)
        setCampaignSaveStatus({
          type: 'error',
          message: error?.response?.data?.message || error?.response?.data?.error || 'Failed to update SMS message'
        })
      }
    }
  }
  const updateRCSMessage = async (campaignId, smsCampaignChannelId, rcsPayload, validate, type) => {
    setCampaignSaveStatus(null)
    try {
      const response = await api.put(
        `${campaignUrl}/${campaignId}/channeldata/${rcsChannelId}`,
        rcsPayload,

        {
          params: {
            validation: false
          },
          skipGlobalToast: true
        }
      )

      setIsRCSContentModified(false)
      let channelRCSId = response?.data?.data?.channelId

      fetchRcsTranslation(channelRCSId, type)

      if (response?.status === 200) {
        setCampaignSaveStatus({ type: 'success', message: 'RCS Campaign message updated successfully' })
        return true
      }
      return false
    } catch (error) {
      if (error) {
        setIsRCSContentModified(true)
        setCampaignSaveStatus({
          type: 'error',
          message: error?.response?.data?.message || error?.response?.data?.error || 'Failed to update RCS message'
        })
      }
    }
  }

  const updateWhatsappMessage = async (campaignId, channelId, whatsappPayload, validate, type) => {
    setCampaignSaveStatus(null)
    try {
      const response = await api.put(
        `${campaignUrl}/${campaignId}/channeldata/${channelId}`,
        whatsappPayload,

        {
          params: {
            // validation: validationStatus
            validation: false
          },
          skipGlobalToast: true
        }
      )

      setIsWhatsappContentModified(false)
      const channelWhatsappId = response?.data?.data?.channelId
      fetchWhatsappTranslation(channelWhatsappId, type)

      if (response?.status === 200) {
        setCampaignSaveStatus({ type: 'success', message: 'Whatsapp Campaign message updated successfully' })
        return true
      }
      return false
    } catch (error) {
      if (error) {
        setIsWhatsappContentModified(true)
        setCampaignSaveStatus({
          type: 'error',
          message: error?.response?.data?.message || error?.response?.data?.error || 'Failed to update Whatsapp message'
        })
      }
    }
  }

  const updateEmailMessage = async (campaignId, emailCampaignChannelId, emailPayload, validate, type) => {
    setCampaignSaveStatus(null)
    try {
      const response = await api.put(
        `${campaignUrl}/${campaignId}/channeldata/${emailChannelId}`,
        emailPayload,

        {
          params: {
            // validation: validationStatus
            validation: false
          },
          skipGlobalToast: true
        }
      )
      setIsEmailContentModified(false)
      const channelEmailId = response?.data?.data?.channelId
      fetchEmailTranslation(channelEmailId, type)
      // sendCampaignMailToMailpit(emailValue)

      if (response?.data?.data.id) {
        const campaignMessage_id = response?.data?.data.id

        setCampaignMessageID(campaignMessage_id)
      }
      if (response?.status === 200) {
        setCampaignSaveStatus({ type: 'success', message: 'Campaign Email updated successfully' })
        return true
      }
      return false
    } catch (error) {
      if (error) {
        setIsEmailContentModified(true)
        setCampaignSaveStatus({
          type: 'error',
          message: error?.response?.data?.message || error?.response?.data?.error || 'Failed to update campaign email'
        })
      }
    }
  }

  const validateCampaignPayload = async (type, inputPayload) => {
    try {
      const inputData = {
        action: type,
        data: inputPayload
      }

      const response = await api.post(`${campaignUrl}/validate-payload`, inputData)
      if (response?.status === 200) {
        showSuccess('Campaign validated successfully')
        return true
      }
      return false
    } catch {
      // errors are already shown by the api client's response interceptor
    }
  }

  const getCampaignCategories = async () => {
    try {
      const response = await api.get(`${campaignCategoryUrl}`)

      const categories = response?.data?.data

      setCampaignCategoryList(categories)
    } catch {
      // errors are already shown by the api client's response interceptor
    }
  }

  const getEditorList = async () => {
    api
      .get(`${campaignUrl}/editors`)
      .then(async _response => {})
      .catch(_err => {})
  }

  const formatToDateString = dateString => {
    if (!dateString) {
      return '-'
    }

    const date = new Date(dateString)

    const formattedDate = date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })

    const formattedTime = date
      .toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
      .replace('am', 'AM')
      .replace('pm', 'PM')

    return `${formattedDate} ${formattedTime}`
  }
  const determineDefaultValue = () => {
    if (campaignDirection === 'f') {
      if (enableEmail) {
        return 'email'
      }
      if (enableWhatsapp) {
        return 'whatsapp'
      }
      if (enableSms) {
        return 'sms'
      }
      if (enableRCS) {
        return 'rcs'
      }
    } else if (campaignDirection === 'b') {
      if (enableRCS) {
        return 'rcs'
      }
      if (enableSms) {
        return 'sms'
      }
      if (enableWhatsapp) {
        return 'whatsapp'
      }
      if (enableEmail) {
        return 'email'
      }
    }
    return ''
  }

  const determineDefaultChannelStep = () => {
    if (campaignDirection === 'f') {
      if (enableEmail) {
        return 1.1
      }
      if (enableWhatsapp) {
        return 1.2
      }
      if (enableSms) {
        return 1.3
      }
      if (enableRCS) {
        return 1.4
      }
    } else if (campaignDirection === 'b') {
      if (enableRCS) {
        return 1.4
      }
      if (enableSms) {
        return 1.3
      }
      if (enableWhatsapp) {
        return 1.2
      }
      if (enableEmail) {
        return 1.1
      }
    }
    return 0
  }

  function formatDate(timestamp) {
    // Convert to Date object
    const date = new Date(timestamp)

    // Format the date
    const formattedDate = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`

    // Format the time
    const hours = date.getHours()
    const formattedTime = `${hours % 12 || 12}:${date.getMinutes().toString().padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`

    // Combine date and time
    return `${formattedDate} ${formattedTime}`
  }

  // const getChannelIdList = selectedChannelsList => {
  //   return selectedChannelsList.map(channel => ({ channelId: channel.channelId }))
  // }
  // const getCampaignChannelIdList = selectedChannelsList => {
  //   return selectedChannelsList.map(channel => ({
  //     campaignChannelId: channel.campaignChannelId,
  //     channelId: channel.channelId
  //   }))
  // }
  const getChannelIdList = selectedChannelsList => {
    // Create flow: only send active channels
    return selectedChannelsList
      .filter(channel => channel.channelStatus === 'active')
      .map(channel => ({ channelId: channel.channelId }))
  }

  const getCampaignChannelIdList = selectedChannelsList => {
    // Update flow: only send active channels with their campaignChannelId
    return selectedChannelsList
      .filter(channel => channel.channelStatus === 'active')
      .map(channel => ({
        ...(channel.campaignChannelId ? { campaignChannelId: channel.campaignChannelId } : {}),
        channelId: channel.channelId
      }))
  }
  const enableCampaignChannels = async (campaignId, channelIds) => {
    try {
      const queryParams = channelIds.map(id => `channelId=${id}`).join('&')

      const response = await api.put(`${campaignUrl}/${campaignId}/campaignChannels?${queryParams}`, {})
      if (response?.status === 200 || response?.status === 204) {
        // nothing to do on success
      } else {
        showError('Failed to disable channels')
      }
    } catch {
      // errors are already shown by the api client's response interceptor
    }
  }

  const disableCampaignChannels = async (campaignId, campaignChannelIds) => {
    try {
      const queryParams = campaignChannelIds.map(id => `campaignChannelId=${id}`).join('&')

      const response = await api.delete(`${campaignUrl}/${campaignId}/campaignChannels?${queryParams}`)

      if (response?.status === 200 || response?.status === 204) {
        // nothing to do on success
      } else {
        showError('Failed to disable channels')
      }
    } catch {
      // errors are already shown by the api client's response interceptor
    }
  }

  const updateChannelStates = channels => {
    setEnableEmail(
      channels?.some(channel => channel.channelName.toLowerCase() === 'email' && channel.channelStatus === 'active')
    )
    setEnableWhatsapp(
      channels?.some(channel => channel.channelName.toLowerCase() === 'whatsapp' && channel.channelStatus === 'active')
    )
    setEnableSms(
      channels?.some(channel => channel.channelName.toLowerCase() === 'sms' && channel.channelStatus === 'active')
    )
    setEnableRCS(
      channels?.some(channel => channel.channelName.toLowerCase() === 'rcs' && channel.channelStatus === 'active')
    )

    const emailChannel = channels?.find(
      channel => channel.channelName.toLowerCase() === 'email' && channel.channelStatus === 'active'
    )
    const whatsappChannel = channels?.find(
      channel => channel.channelName.toLowerCase() === 'whatsapp' && channel.channelStatus === 'active'
    )
    const smsChannel = channels?.find(
      channel => channel.channelName.toLowerCase() === 'sms' && channel.channelStatus === 'active'
    )
    const rcsChannel = channels?.find(
      channel => channel.channelName.toLowerCase() === 'rcs' && channel.channelStatus === 'active'
    )
    setEmailChannelId(emailChannel ? emailChannel.channelId : null)
    setWhatsappChannelId(whatsappChannel ? whatsappChannel.channelId : null)
    setRCSChannelId(rcsChannel ? rcsChannel.channelId : null)
    setSmsChannelId(smsChannel ? smsChannel.channelId : null)
    // setEmailChannelId( emailChannel?.channelId )
    // setWhatsappChannelId(whatsappChannel ? whatsappChannel.channelId : null)
    // setSmsChannelId(smsChannel ? smsChannel.channelId : null)

    setEmailCampaignChannelId(emailChannel ? emailChannel.campaignChannelId : null)
    setWhatsappCampaignChannelId(whatsappChannel ? whatsappChannel.campaignChannelId : null)
    setSmsCampaignChannelId(smsChannel ? smsChannel.campaignChannelId : null)

    setEmailCampaignContentId(emailChannel?.campaignChannelTranslation?.campaignChannelTranslationId ?? null)
    setWhatsappCampaignContentId(whatsappChannel?.campaignChannelTranslation?.campaignChannelTranslationId ?? null)
    setSmsCampaignContentId(smsChannel?.campaignChannelTranslation?.campaignChannelTranslationId ?? null)
  }

  const getOminiChannelData = async _segmentName => {
    // try {
    //   const inputData = {
    //     segment_name: segmentName
    //   }
    //   const response = await api.post(`${segmentsListUrl}/channel-statistics`, inputData)
    //
    //   setCampaignChannelData(response?.data?.data)
    // } catch {}
  }
  const values = useMemo(() => ({
    createCampaign,
    determineDefaultValue,
    getCampaignMessage,
    updateChannelStates,
    getCampaignList,
    getArchiveList,
    determineDefaultChannelStep,
    getCampaign,
    updateSMSMessage,
    updateRCSMessage,
    updateWhatsappMessage,
    updateEmailMessage,
    validateCampaignPayload,
    getEditorList,
    updateCampaign,
    deleteCampaign,
    cloneCampaign,
    updateCampaignRecordStatus,
    formatToDateString,
    formatDate,
    campaignBulkAction,
    getChannelIdList,
    updateFinalCampaign,
    getCampaignCategories,
    getOminiChannelData,
    getPromotionalCampaignFilterList,
    archivePromotionalCampaign

    // getEmailPayload: getEmailPayload
  }))

  return <PromotionalCampaignContext.Provider value={values}>{children}</PromotionalCampaignContext.Provider>
}

export { PromotionalCampaignContext, PromotionalCampaignProvider }
